import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Spinner, WarningCircle } from "@phosphor-icons/react";
import api from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const razorpayOrderId = params.get("razorpay_order_id");
  const razorpaySubId = params.get("razorpay_subscription_id");
  const [state, setState] = useState({ phase: "polling", amount: null, name: "", currency: "usd", recurring: false });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId && !razorpayOrderId && !razorpaySubId) {
      setState({ phase: "error", amount: null, name: "", currency: "usd", recurring: false });
      return;
    }
    const url = sessionId
      ? `/payments/status/${sessionId}`
      : razorpayOrderId
        ? `/payments/razorpay/status/${razorpayOrderId}`
        : `/payments/razorpay/subscription/status/${razorpaySubId}`;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(url);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ phase: "paid", amount: data.amount, name: data.donor_name, currency: data.currency || "usd", recurring: !!data.recurring });
          return;
        }
      } catch {
        if (cancelled) return;
      }
      attempts.current += 1;
      if (attempts.current < 12) {
        setTimeout(poll, 2000);
      } else if (!cancelled) {
        setState((s) => ({ ...s, phase: "pending" }));
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, razorpayOrderId, razorpaySubId]);

  const sym = state.currency === "INR" ? "₹" : "$";

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="noise-overlay" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="max-w-lg w-full border border-forest/20 bg-bone p-10 text-center shadow-xl"
      >
        {state.phase === "polling" && (
          <>
            <Spinner size={48} className="text-terra animate-spin mx-auto mb-6" />
            <h1 className="font-serif font-bold tracking-tighter text-forest text-4xl" data-testid="payment-status-heading">
              Confirming your gift…
            </h1>
            <p className="text-base text-moss mt-4">Hold on a moment while we confirm your payment.</p>
          </>
        )}
        {state.phase === "paid" && (
          <>
            <CheckCircle size={56} weight="fill" className="text-terra mx-auto mb-6" />
            <h1 className="font-serif font-bold tracking-tighter text-forest text-4xl" data-testid="payment-status-heading">
              Thank you{state.name ? `, ${state.name.split(" ")[0]}` : ""}.
            </h1>
            <p className="text-base text-moss mt-4 leading-relaxed" data-testid="payment-success-message">
              {state.recurring ? (
                <>Your monthly gift of <span className="font-bold text-forest">{sym}{Number(state.amount).toLocaleString("en-IN")}</span> is now active.
                You&apos;ll be charged automatically each month — and every month, a classroom gets a little brighter.</>
              ) : (
                <>Your gift of <span className="font-bold text-forest">{sym}{Number(state.amount).toLocaleString("en-IN")}</span> is confirmed.
                A receipt is on its way to your inbox — and a classroom just got a little brighter.</>
              )}
            </p>
          </>
        )}
        {state.phase === "pending" && (
          <>
            <Spinner size={48} className="text-terra animate-spin mx-auto mb-6" />
            <h1 className="font-serif font-bold tracking-tighter text-forest text-4xl" data-testid="payment-status-heading">
              Almost there.
            </h1>
            <p className="text-base text-moss mt-4 leading-relaxed">
              Your payment is still being confirmed by the bank. It will reflect shortly — no need to donate again.
            </p>
          </>
        )}
        {state.phase === "error" && (
          <>
            <WarningCircle size={56} weight="fill" className="text-terra mx-auto mb-6" />
            <h1 className="font-serif font-bold tracking-tighter text-forest text-4xl" data-testid="payment-status-heading">
              Something&apos;s missing.
            </h1>
            <p className="text-base text-moss mt-4">We couldn&apos;t find this donation session.</p>
          </>
        )}
        <Link to="/" data-testid="payment-home-link"
          className="inline-block mt-8 bg-forest text-bone font-bold px-8 py-4 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200">
          Back to Learning Lights
        </Link>
      </motion.div>
    </div>
  );
}
